import type { JSX } from 'react';
import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { api, ApiError, deleteErrorMessage } from '@/lib/api-client';
import { ForbiddenNotice } from './forbidden-notice';

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (row: T) => ReactNode;
  width?: string;
}

export interface CrudTableProps<T extends { id: string }> {
  title: string;
  endpoint: string;
  columns: Column<T>[];
  renderForm: (
    initial: Partial<T> | null,
    onSubmit: (values: Partial<T>) => void,
    onCancel: () => void,
  ) => ReactNode;
  itemTitle: (row: T) => string;
  canEdit?: boolean;
  canDelete?: boolean;
  extraActions?: ReactNode;
  /** Replaces the generic bound-to-segments sentence on a refused (409) delete. */
  deleteConflictMessage?: string;
}

// Default cell rendering for columns without a custom `render`. The value is
// `unknown`, so narrow before stringifying to avoid `[object Object]`.
function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return JSON.stringify(value);
}

export function CrudTable<T extends { id: string }>(props: CrudTableProps<T>): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Partial<T> | null>(null);
  const [creating, setCreating] = useState<boolean>(false);

  const list = useQuery({
    queryKey: [props.endpoint],
    queryFn: () => api.get<T[]>(props.endpoint),
  });

  const create = useMutation({
    mutationFn: (values: Partial<T>) => api.post<T>(props.endpoint, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [props.endpoint] });
      setCreating(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<T> }) =>
      api.patch<T>(`${props.endpoint}/${id}`, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [props.endpoint] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${props.endpoint}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [props.endpoint] }),
    onError: (error: unknown) => alert(deleteErrorMessage(error, props.deleteConflictMessage)),
  });

  // A role revoked mid-session surfaces as a 403 on the next fetch — explain it instead of
  // printing the raw error into the table (EW-65).
  if (list.error instanceof ApiError && list.error.status === 403) {
    return <ForbiddenNotice />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-heading">{props.title}</h2>
        <div className="flex items-center gap-2">
          {props.extraActions}
          <button className="btn-primary" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Új
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              {props.columns.map((c) => (
                <th key={String(c.key)} className="px-4 py-2.5" style={{ width: c.width }}>
                  {c.label}
                </th>
              ))}
              <th className="w-24 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.isLoading && (
              <tr>
                <td colSpan={props.columns.length + 1} className="px-4 py-6 text-center text-slate-500">
                  Betöltés…
                </td>
              </tr>
            )}
            {list.isError && (
              <tr>
                <td colSpan={props.columns.length + 1} className="px-4 py-6 text-center text-rose-600">
                  Hiba: {String(list.error)}
                </td>
              </tr>
            )}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={props.columns.length + 1} className="px-4 py-6 text-center text-slate-500">
                  Nincs adat.
                </td>
              </tr>
            )}
            {list.data?.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                {props.columns.map((c) => (
                  <td key={String(c.key)} className="px-4 py-2.5">
                    {c.render ? c.render(row) : formatCell((row as Record<string, unknown>)[c.key as string])}
                  </td>
                ))}
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    {props.canEdit !== false && (
                      <button
                        title="Szerkeszt"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        onClick={() => setEditing(row)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {props.canDelete !== false && (
                      <button
                        title="Töröl"
                        className="rounded p-1 text-rose-500 hover:bg-rose-50"
                        onClick={() => {
                          if (confirm(`Törlés: "${props.itemTitle(row)}"?`)) remove.mutate(row.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay)' }}
        >
          <div className="card w-full max-w-lg p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-heading">
                {editing ? 'Szerkesztés' : 'Új létrehozás'}
              </h3>
              <button
                className="rounded p-1 text-slate-400 hover:bg-slate-100"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {props.renderForm(
              editing,
              (values) => {
                if (editing?.id) update.mutate({ id: editing.id, values });
                else create.mutate(values);
              },
              () => {
                setCreating(false);
                setEditing(null);
              },
            )}
            {(create.isError || update.isError) && (
              <p className="mt-3 text-sm text-rose-600">{String(create.error ?? update.error)}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
